import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { SessionService } from '../session/session.service';
import { LoggingService } from '../logging/logging.service';
import { ScriptService } from '../script/script.service';
import { AuthService } from '../auth/auth.service';

interface AuthInfo {
  type: string;
  token: string | null;
}

interface HostAPI {
  /**
   * Retrieves the current authentication information.
   * Ensures the session is refreshed if needed before returning the token.
   */
  getAuthInfo: () => Promise<AuthInfo>;

  /**
   * Forces a token refresh and returns new authentication information.
   * The chat widget calls this method when the current token expires to obtain a fresh token.
   */
  refreshToken?: () => Promise<AuthInfo>;
}

declare global {
  interface Window {
    hostAPI?: HostAPI;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ChatWidgetService {
  private readonly sessionService = inject(SessionService);
  private readonly logger = inject(LoggingService);
  private readonly scriptService = inject(ScriptService);
  private readonly authService = inject(AuthService);

  private cdnUrl?: string;

  /**
   * Load the chat widget script from CDN.
   *
   * @param cdnUrl
   *
   * @returns
   */
  public async loadScript(cdnUrl: string): Promise<void> {
    this.cdnUrl = cdnUrl;

    try {
      await this.scriptService.loadScript({
        src: cdnUrl,
        attributes: {
          type: 'module',
          async: 'true'
        }
      });

      this.logger.info('Chat widget script loaded successfully');
    } catch (error) {
      this.logger.error('Failed to load chat widget script', error);
      throw error;
    }
  }

  /**
   * Initialize the hostAPI for authentication.
   *
   */
  public initializeHostAPI(): void {
    window.hostAPI = {
      getAuthInfo: this.getAuthToken.bind(this),
      refreshToken: this.getAuthToken.bind(this)
    };

    this.logger.info('Chat widget hostAPI initialized');
  }

  /**
   * Initialize the chat widget by setting up the host API and loading the widget script.
   *
   * @param cdnUrl
   */
  public async initialize(cdnUrl: string): Promise<void> {
    try {
      this.initializeHostAPI();

      await this.loadScript(cdnUrl);

      this.logger.info('Chat widget initialized successfully');
    } catch (error) {
      this.logger.error('Failed to initialize chat widget', error);
      throw error;
    }
  }

  /**
   * Cleanup chat widget resources.
   */
  public cleanup(): void {
    try {
      if (this.cdnUrl) {
        this.scriptService.unloadScript(this.cdnUrl);
      }

      if (window.hostAPI) {
        delete window.hostAPI;
      }

      this.logger.info('Chat widget cleaned up');
    } catch (error) {
      this.logger.warn('Failed to cleanup chat widget', error);
    }
  }

  /**
   *
   * @param newAccessToken
   */
  public handleSessionRefresh(newAccessToken: string | null): void {
    // If session has changed and access token is null, chat widget expects terminateSession event to clear its session state
    if (!newAccessToken) {
      document.dispatchEvent(new CustomEvent('terminateSession'));

      return;
    }

    // For active sessions chat widget expects authChanged event with the new token pass in params
    document.dispatchEvent(
      new CustomEvent('authChanged', { detail: { type: 'Bearer', token: newAccessToken } })
    );
  }

  /**
   * Retrieves authentication information for the chat widget.
   *
   * This method ensures the session is refreshed if needed before returning.
   *
   * @returns A promise that resolves to an AuthInfo object containing the token type and access token.
   */
  private async getAuthToken(): Promise<AuthInfo> {
    await firstValueFrom(this.authService.refreshSessionIfNeeded());

    const userSession = this.sessionService.getUserSession();
    const token = userSession?.getAccessToken() || null;

    return {
      type: 'Bearer',
      token
    };
  }
}
